import React, { useEffect, useRef, useCallback, useMemo } from 'react';

interface BreathingOrbProps {
  size?: number;
  momentumLevel?: number;
  isActive?: boolean;
  className?: string;
}

interface Layer {
  baseRadius: number;
  opacity: number;
  hue: number;
}

const BreathingOrb: React.FC<BreathingOrbProps> = ({ 
  size = 220, 
  momentumLevel = 0, 
  isActive = true,
  className = '' 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const breathPhaseRef = useRef<number>(0);

  // Exact 0.1 Hz frequency calculation
  // At 60fps: 0.1 Hz = 0.1 cycles/second = 0.1 * 2π / 60 = 0.0105 radians per frame
  const BREATH_PHASE_INCREMENT = 0.0105;
  
  // Layer configuration with specific hues - memoized to prevent re-renders
  const layers = useMemo((): Layer[] => [
    { baseRadius: 80, opacity: 0.1, hue: 280 },
    { baseRadius: 65, opacity: 0.15, hue: 270 },
    { baseRadius: 50, opacity: 0.2, hue: 260 },
    { baseRadius: 35, opacity: 0.25, hue: 250 }
  ], []);

  // Calculate hue shift based on momentum level
  const getHueShift = useCallback(() => {
    // Shift warmer (towards yellow/orange) as momentum increases
    return Math.min(momentumLevel * 3, 30); // Max 30 degree shift
  }, [momentumLevel]);

  const createRadialGradient = useCallback((
    ctx: CanvasRenderingContext2D, 
    centerX: number, 
    centerY: number, 
    radius: number, 
    opacity: number,
    hue: number
  ) => {
    const adjustedHue = hue + getHueShift();
    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
    
    // Create smooth radial gradient
    gradient.addColorStop(0, `hsla(${adjustedHue}, 60%, 70%, ${opacity * 1.2})`);
    gradient.addColorStop(0.3, `hsla(${adjustedHue}, 55%, 65%, ${opacity})`);
    gradient.addColorStop(0.7, `hsla(${adjustedHue}, 50%, 60%, ${opacity * 0.8})`);
    gradient.addColorStop(1, `hsla(${adjustedHue}, 45%, 55%, 0)`);
    
    return gradient;
  }, [getHueShift]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isActive) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    // Calculate breathing cycles
    const mainBreath = Math.sin(breathPhaseRef.current) * 0.5 + 0.5; // 0-1 range
    const secondaryBreath = Math.sin(breathPhaseRef.current * 2) * 0.1; // -0.1 to 0.1
    const breathCycle = mainBreath + secondaryBreath;
    
    // Enable shadow/glow effects
    ctx.shadowBlur = 30;
    ctx.globalCompositeOperation = 'screen';
    
    // Draw layers from largest to smallest
    layers.forEach((layer) => {
      const radius = layer.baseRadius * (0.8 + breathCycle * 0.4) * (size / 220);
      const gradient = createRadialGradient(ctx, centerX, centerY, radius, layer.opacity, layer.hue);
      
      ctx.fillStyle = gradient;
      ctx.shadowColor = `hsl(${layer.hue + getHueShift()}, 60%, 70%)`;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fill();
    });
    
    // Draw center core that pulses between 3-5px
    const coreRadius = (3 + breathCycle * 2) * (size / 220);
    const coreHue = 260 + getHueShift();
    const coreGradient = createRadialGradient(ctx, centerX, centerY, coreRadius * 3, 0.9, coreHue);
    
    ctx.fillStyle = coreGradient;
    ctx.shadowBlur = 20;
    ctx.shadowColor = `hsl(${coreHue}, 70%, 80%)`;
    ctx.beginPath();
    ctx.arc(centerX, centerY, coreRadius, 0, Math.PI * 2);
    ctx.fill();
    
    // Reset composite operation and shadow
    ctx.globalCompositeOperation = 'source-over';
    ctx.shadowBlur = 0;
  }, [size, isActive, layers, createRadialGradient, getHueShift]);

  const animate = useCallback(() => {
    if (!isActive) return;
    
    // Increment breath phase by exactly 0.0105 radians per frame for 0.1 Hz
    breathPhaseRef.current += BREATH_PHASE_INCREMENT;
    
    // Keep phase within reasonable bounds
    if (breathPhaseRef.current > Math.PI * 2 * 1000) {
      breathPhaseRef.current -= Math.PI * 2 * 1000;
    }
    
    draw();
    animationRef.current = requestAnimationFrame(animate);
  }, [isActive, draw, BREATH_PHASE_INCREMENT]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size with device pixel ratio for crisp rendering
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
    }
    
    // Start animation
    if (isActive) {
      animationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [size, isActive, animate]);

  // Handle active state changes
  useEffect(() => {
    if (isActive && !animationRef.current) {
      animationRef.current = requestAnimationFrame(animate);
    } else if (!isActive && animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = undefined;
    }
  }, [isActive, animate]);

  return (
    <div className={`inline-block ${className}`} style={{ width: size, height: size }}>
      <canvas
        ref={canvasRef}
        className="block"
        style={{
          width: '100%',
          height: '100%',
          filter: 'blur(0.5px)', // Slight blur for softer appearance
        }}
      />
    </div>
  );
};

export default BreathingOrb;